import DataLoader from 'dataloader'
import { orpcPublicClient } from './client'
import { tackBatch } from './generated/sdk.gen'
import type { TackBatchResponse } from './generated/types.gen'

export type BatchTackItem = TackBatchResponse['data'][number]

/** Server caps /v2/tack/batch at 100 ids per request. */
const CHUNK_LIMIT = 100

export interface LoaderKey {
  id: string
  lang: string
}

const cacheKeyOf = (k: LoaderKey): string => `${k.id}|${k.lang}`

/**
 * DataLoader `batchLoadFn` for /v2/tack/batch. Exported for unit testing.
 *
 * - Groups input keys by `lang` (the API takes one lang per call) and dedupes
 *   ids per group via the natural set semantics of `Map.values()`.
 * - Returns an output array of the *same length and order* as `keys`. Ids the
 *   server omits resolve to `null` at their input slot.
 * - On RPC failure, the rejection propagates to all queued callers — React
 *   Query then marks those queries as errored and retries per its own
 *   policy (no negative-caching of network failures).
 */
export async function batchLoadTacks(
  keys: readonly LoaderKey[],
): Promise<Array<BatchTackItem | null>> {
  const idsByLang = new Map<string, string[]>()
  for (const k of keys) {
    const list = idsByLang.get(k.lang) ?? []
    list.push(k.id)
    idsByLang.set(k.lang, list)
  }

  const found = new Map<string, BatchTackItem>() // `${id}|${lang}` → item
  await Promise.all(
    [...idsByLang].map(async ([lang, ids]) => {
      const { data } = await tackBatch({
        client: orpcPublicClient,
        query: { ids, lang },
        throwOnError: true,
      })
      for (const item of data?.data ?? []) {
        found.set(`${item.id}|${lang}`, item)
      }
    }),
  )

  return keys.map(k => found.get(cacheKeyOf(k)) ?? null)
}

/**
 * Coalesces concurrent /v2/tack/batch hydrations across the page. DataLoader
 * collects every `loader.load(...)` call within a microtask, dispatches them
 * as one `batchLoadFn` invocation, and resolves each caller with its own
 * item.
 *
 * `cache: false` is intentional: React Query owns per-id caching under
 * `['tackItem', id, lang]` (see `useTacksByIds`). Enabling DataLoader's
 * cache would shadow RQ refetches/invalidations — when RQ explicitly
 * invalidates a query, the next queryFn → `loadTack` would return a stale
 * cached Promise instead of re-fetching. So this layer is purely a batcher;
 * the in-tick same-key dedup that DataLoader's cache would otherwise
 * provide is already covered by RQ's per-key in-flight dedup.
 *
 * Module-level singleton: each browser tab is its own module instance, and
 * the only call site (ArtworkPreviewCardSimple, gated on the show-tags
 * experiment + auth) doesn't fire during SSR.
 */
const tackLoader = new DataLoader<LoaderKey, BatchTackItem | null, string>(
  batchLoadTacks,
  {
    cache: false,
    maxBatchSize: CHUNK_LIMIT,
    cacheKeyFn: cacheKeyOf,
  },
)

/** Returns `null` when the server omits an id (unknown id). */
export function loadTack(
  id: string,
  lang: string,
): Promise<BatchTackItem | null> {
  return tackLoader.load({ id, lang })
}
